/**
 * useAuth hook
 * Manages authentication state and post-login access check.
 * After MSAL login, calls /api/access/login to determine routing:
 *   - 'dashboard'        → returning user, go to /dashboard
 *   - 'setup-required'  → first-time user, go to /onboarding
 *   - 'license-error'   → no valid license
 *   - 'permissions-error' → no SP access
 */

import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRedirect, isAuthenticated } from '../services/authService';
import { checkLoginAccess } from '../services/bootstrapService';
import { trackFeatureUsage, trackException, clearUserContext, FeatureEvent, EventProp, ExceptionPhase, ExceptionProp } from '../observability';
import type { LoginAccessResponse } from '../../../shared/dto/bootstrap';

export interface AuthState {
    isAuthenticating: boolean;
    isAuthorized: boolean | null;
    route: LoginAccessResponse['route'] | null;
    error: string | null;
}

export function useAuth() {
    const { instance } = useMsal();
    const [authState, setAuthState] = useState<AuthState>({
        isAuthenticating: false,
        isAuthorized: null,
        route: null,
        error: null,
    });

    const handleLogin = async (): Promise<LoginAccessResponse> => {
        setAuthState((prev) => ({ ...prev, isAuthenticating: true, error: null }));

        try {
            // Step 1: MSAL login if not already authenticated.
            // Use redirect (not popup) so it works even when browsers block popups.
            // After successful login, AAD redirects back to the app and
            // LandingPage.handlePostRedirect() processes the result via handleRedirectPromise().
            if (!isAuthenticated()) {
                await loginRedirect();
                // loginRedirect navigates the page away — code below won't execute
                // Return a placeholder so TypeScript is happy; it will never be reached
                return { allowed: false, reason: 'Redirecting...', route: 'license-error' } as LoginAccessResponse;
            }

            // Step 2: Check access via /api/access/login
            // This validates license, onboarding status, and SP access in one call
            const result = await checkLoginAccess();

            setAuthState({
                isAuthenticating: false,
                isAuthorized: result.allowed,
                route: result.route,
                error: result.allowed ? null : result.reason,
            });

            trackFeatureUsage(FeatureEvent.Login, { [EventProp.Route]: result.route, [EventProp.Allowed]: String(result.allowed) });

            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
            setAuthState({
                isAuthenticating: false,
                isAuthorized: false,
                route: null,
                error: errorMessage,
            });
            trackException(error, { [ExceptionProp.Phase]: ExceptionPhase.Login });
            throw error;
        }
    };

    const handleLogout = async () => {
        trackFeatureUsage(FeatureEvent.Logout);
        clearUserContext();
        await instance.logoutPopup();
        setAuthState({
            isAuthenticating: false,
            isAuthorized: null,
            route: null,
            error: null,
        });
    };

    return {
        ...authState,
        handleLogin,
        handleLogout,
    };
}
